import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authApi } from '../../api/auth.api';
import { useAuth } from '../../store/AuthContext';
import { getDashboardRoute } from '../../utils/roleHelpers';
import { invalidateMenuPreferencesCache } from '../../utils/menuPreferences';
import { Spinner } from '../../components/ui/index';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import './AuthPages.css';

// Subtle desktop-only cursor: a dot + trailing ring, eased with rAF.
// Disabled on touch devices and when the user prefers reduced motion.
function useCustomCursor(containerRef) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isCoarsePointer = window.matchMedia('(hover: none), (pointer: coarse)').matches;
    if (prefersReducedMotion || isCoarsePointer) return undefined;

    const dot = document.createElement('div');
    const ring = document.createElement('div');
    dot.className = 'pj-cursor-dot';
    ring.className = 'pj-cursor-ring';
    container.appendChild(dot);
    container.appendChild(ring);

    let mouseX = -100, mouseY = -100;
    let ringX = -100, ringY = -100;
    let rafId;

    const handleMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      dot.style.transform = `translate3d(${mouseX}px, ${mouseY}px, 0)`;
    };

    const handleOver = (e) => {
      const interactive = e.target.closest('a, button, input, label, .pj-auth-link');
      ring.classList.toggle('pj-cursor-hover', !!interactive);
    };

    const tick = () => {
      ringX += (mouseX - ringX) * 0.18;
      ringY += (mouseY - ringY) * 0.18;
      ring.style.transform = `translate3d(${ringX}px, ${ringY}px, 0)`;
      rafId = requestAnimationFrame(tick);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseover', handleOver);
    rafId = requestAnimationFrame(tick);
    document.body.classList.add('pj-cursor-active');

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseover', handleOver);
      cancelAnimationFrame(rafId);
      document.body.classList.remove('pj-cursor-active');
      dot.remove();
      ring.remove();
    };
  }, [containerRef]);
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { setUser, loadProfile, user, loading: authLoading } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [shake, setShake] = useState(false);
  const [waitTimedOut, setWaitTimedOut] = useState(false);
  const rootRef = useRef(null);

  useCustomCursor(rootRef);

  useEffect(() => { setTimeout(() => setMounted(true), 60); }, []);

  // Already logged in (valid session_id cookie survived a page reload /
  // revisit) — bounce straight to the dashboard instead of showing the form.
  useEffect(() => {
    if (!authLoading && user) navigate(getDashboardRoute(user.type), { replace: true });
  }, [user, authLoading, navigate]);

  // Don't make the user stare at a spinner if the session check is slow
  // (server hiccup, slow network) — give up after 2s and show the login
  // form so they can just log in again. If the check resolves later with a
  // valid session, the effect above still fires and redirects normally.
  useEffect(() => {
    if (!authLoading) return;
    const t = setTimeout(() => setWaitTimedOut(true), 2000);
    return () => clearTimeout(t);
  }, [authLoading]);

  const triggerShake = () => { setShake(true); setTimeout(() => setShake(false), 500); };

  const handleLogin = (e) => {
    e.preventDefault();
    if (!username || !password) { setError('لطفاً نام کاربری و رمز عبور را وارد کنید.'); triggerShake(); return; }
    setLoading(true); setError('');
    authApi.login(username.trim().toLowerCase(), password)
      .then(() => {
        invalidateMenuPreferencesCache();
        return loadProfile();
      })
      .then((profile) => {
        if (profile) { setUser(profile); navigate(getDashboardRoute(profile.type)); }
        else { setError('خطا در دریافت اطلاعات کاربر.'); triggerShake(); }
      })
      .catch((err) => {
        const status = err.response?.status;
        if (status === 403) setError('نام کاربری یا رمز عبور اشتباه است.');
        else setError('خطا در اتصال به سرور. لطفاً دوباره تلاش کنید.');
        triggerShake();
      })
      .finally(() => setLoading(false));
  };

  if ((authLoading && !waitTimedOut) || user) {
    return (
      <div className="pj-auth" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size={28} />
      </div>
    );
  }

  return (
    <div className="pj-auth" ref={rootRef}>
      {/* Brand panel (desktop only) */}
      <div className="pj-auth-brand">
        <svg className="pj-auth-brand-visual" viewBox="0 0 400 400" preserveAspectRatio="none" aria-hidden="true">
          <line x1="60" y1="80" x2="180" y2="150" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
          <line x1="180" y1="150" x2="320" y2="90" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
          <line x1="180" y1="150" x2="150" y2="280" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
          <line x1="150" y1="280" x2="300" y2="320" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
          <line x1="150" y1="280" x2="40" y2="330" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
          <circle cx="60" cy="80" r="4" fill="#29AAE1" />
          <circle cx="180" cy="150" r="5" fill="#fff" />
          <circle cx="320" cy="90" r="4" fill="#29AAE1" />
          <circle cx="150" cy="280" r="5" fill="#fff" />
          <circle cx="300" cy="320" r="4" fill="#29AAE1" />
          <circle cx="40" cy="330" r="4" fill="#29AAE1" />
        </svg>
        <div className="pj-auth-brand-content">
          <img src="/pejvak-mark.svg" alt="" className="pj-auth-brand-logo" aria-hidden="true" />
          <h2 className="pj-auth-brand-title">سامانه مدیریت ارتباط با مشتری پژواک رایانه</h2>
          <p className="pj-auth-brand-desc">
            ابزاری مطمئن برای تیم‌های پشتیبانی، فروش و عملیات؛ برای پیگیری تیکت‌ها، ماموریت‌ها و ارتباط با مشتریان در یک محیط امن و یکپارچه.
          </p>
        </div>
      </div>

      {/* Form side */}
      <div className="pj-auth-form-side">
        <div className={`pj-auth-form-wrap ${mounted ? 'pj-mounted' : ''}`}>
          <div className="pj-auth-header">
            <img src="/pejvak-mark.svg" alt="پژواک رایانه" className="pj-auth-logo-mobile" />
            <div className="pj-auth-header-text">
              <h1 className="pj-auth-brand-name">پژواک رایانه</h1>
              <p className="pj-auth-tagline">سامانه مدیریت ارتباط با مشتری</p>
            </div>
          </div>

          <div className={`pj-auth-card ${shake ? 'pj-shake' : ''}`}>
            <h2 className="pj-auth-title">ورود به حساب</h2>
            <p className="pj-auth-subtitle">اطلاعات ورود خود را وارد کنید</p>

            {error && (
              <div className="pj-auth-error" role="alert">{error}</div>
            )}

            <form onSubmit={handleLogin}>
              <div className="pj-auth-field">
                <label htmlFor="pj-username" className="pj-auth-label">نام کاربری</label>
                <input
                  id="pj-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="نام کاربری خود را وارد کنید"
                  autoComplete="username"
                  autoFocus
                  dir="ltr"
                  className="pj-ltr-field pj-auth-input"
                />
              </div>

              <div className="pj-auth-field">
                <label htmlFor="pj-password" className="pj-auth-label">رمز عبور</label>
                <div className="pj-auth-password-wrap">
                  <input
                    id="pj-password"
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="رمز عبور خود را وارد کنید"
                    autoComplete="current-password"
                    dir="ltr"
                    className="pj-ltr-field pj-auth-input"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    className="password-toggle"
                    onClick={() => setShowPass(!showPass)}
                    aria-label={showPass ? 'پنهان کردن رمز عبور' : 'نمایش رمز عبور'}
                  >
                    {showPass ? <EyeSlash size={18} weight="regular" /> : <Eye size={18} weight="regular" />}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading} className="pj-auth-button">
                {loading ? <><Spinner size={16} color="#fff" /> در حال ورود...</> : 'ورود به سامانه'}
              </button>
            </form>
          </div>

          <p className="pj-auth-footer">
            حساب ندارید؟{' '}
            <Link to="/sign_up" className="pj-auth-link">ثبت‌نام کنید</Link>
          </p>
          <div className="pj-auth-footer-row">
            <Link to="/forget_password" className="pj-auth-link">فراموشی رمز عبور</Link>
            <a href="mailto:info@pejvakrayane.com" className="pj-auth-link">پشتیبانی</a>
          </div>
        </div>
      </div>
    </div>
  );
}
